import { ulid } from 'ulid';
import { Realtime, type RealtimeChannel  } from 'ably';
import { ABLY_CLIENT_ID } from '@/configuration';
import { apiAddress } from '@/util/http-utils';
import Authorizor from '@/util/authorizor';
import { type SubscriptionTopic, type EventPayload } from "@/api/model/subscription-topic";
import { type HandlerNode, type SwitchboardSubscriptionHandler } from './types';

type Subscriptions = {
    channel: RealtimeChannel;
    nodes: HandlerNode[];
}

/**
 * Switchboard
 * Handles routing of entity-level realtime notifications to subscribers.
 * Switchboard receives activity CRUD notifications.
 */
export class Switchboard {
    private static subscriptions: Map<string, Subscriptions> = new Map<string, Subscriptions>();
    private static ablyClient: Realtime | undefined;
    // private static channel: RealtimeChannel | undefined;

    public static initialize() {
        Switchboard.createClient();
    }

    public static subscribe(topic: SubscriptionTopic, handler: SwitchboardSubscriptionHandler): string {
        const key = Switchboard.makeKey(topic);
        const handlerNode: HandlerNode = {
            identifier: ulid(),
            handler: handler
        }
        let result = handlerNode.identifier;
        let subscriptions: Subscriptions | undefined = Switchboard.subscriptions.get(key);
        // let nodes: HandlerNode[] | undefined = Switchboard.subscriptions.get(key);
        if (!subscriptions) {
            subscriptions = {
                channel: Switchboard.ablyClient?.channels.get(key)!,
                nodes: []

            }
            subscriptions.channel.subscribe(key, (message) => {
                const payload: EventPayload = message.data as EventPayload;
                Switchboard.notify(payload);
            })
        }
        // check if this handler is already assigned
        for (const node of subscriptions.nodes) {
            if (node.handler = handler) {
                result = node.identifier;
                break;
            }
        }
        if (handlerNode.identifier === result) {
            subscriptions.nodes.push(handlerNode);
        }
        Switchboard.subscriptions.set(Switchboard.makeKey(topic), subscriptions);
        return result;
    }

    public static unsubscribe(identifier: string, topic: SubscriptionTopic) {
        const key = Switchboard.makeKey(topic);
        let subscriptions: Subscriptions | undefined = Switchboard.subscriptions.get(key);
        if (!subscriptions) {
            console.warn(`Subscriptions for "${identifier}" not found in Switchboard.unsubscribe.`);
            return;
        }

        subscriptions.nodes = subscriptions.nodes.filter((node) => {
            return node.identifier !== identifier;
        });
        if (subscriptions.nodes.length === 0) {
            subscriptions.channel.unsubscribe();
            Switchboard.subscriptions.delete(key);
            return;
        }
        Switchboard.subscriptions.set(key, subscriptions);
    }

    private static notify(payload: EventPayload) {
        console.log(`NOTIFY (payload=${JSON.stringify(payload)})`);
        const key = Switchboard.makeKey(payload.topic);
        let subscriptions: Subscriptions | undefined = Switchboard.subscriptions.get(key);
        if (!subscriptions) {
            console.warn(`No matching subscriptions found in Switchboard.notify.`);
            return;
        }
        subscriptions.nodes?.map((node) => {
            node.handler(payload);
        });
    }

    private static createClient() {
        const obtainTokenRequest = async () => {
            const response = await fetch(apiAddress("/notification/subscribe"), {
                headers: Authorizor.headers()
            });
            if (!response.ok) {
                console.error("obtainTokenRequest: ", response.statusText);
                throw new Error(response.statusText);
            }
            return await response.json();
        }

        if (Switchboard.ablyClient) {
            return;
        }
        Switchboard.ablyClient = new Realtime({
            clientId: ABLY_CLIENT_ID,
            authCallback: async (_, callback) => {
                let tokenRequest;
                try {
                    tokenRequest = await obtainTokenRequest(); 
                } catch (err) {
                    callback(err as string, null);
                    return;
                }
                callback(null, tokenRequest);
            }
        });
    }

    private static makeKey(topic: SubscriptionTopic): string {
        return `${topic.entity}:${topic.identifier}`;
    }
}